import { useEffect, useLayoutEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOData {
  seo_title: string | null;
  seo_description: string | null;
}

const HOME_META_TITLES: Record<string, string> = {
  en: 'Campervan & Motorhome Turkey | Only 20% Pay to Book',
  tr: 'Karavan Kiralama Türkiye | Sadece %20 Ön Ödeme',
  de: 'Wohnmobil & Camper Türkei | Nur 20% Anzahlung',
  es: 'Alquiler Autocaravanas Turquía | Solo 20% de Reserva',
  it: 'Noleggio Camper & Motorhome Turchia | Solo 20% Anticipo',
  nl: 'Camper & Motorhome huren Turkije | Slechts 20% Aanbetaling',
  ru: 'Аренда автодомов в Турции | Предоплата всего 20%'
};

const HOME_META_DESCRIPTIONS: Record<string, string> = {
  en: 'Explore Turkey with premium campervan rentals. Pick up in Istanbul, Antalya & more. Only 20% payment to book, 24/7 support. Get a quote!',
  tr: "Türkiye'yi lüks karavanlarla keşfedin. İstanbul, Antalya ve dahası. Sadece %20 ön ödeme ile rezerve edin. 7/24 destek ve en iyi fiyatlar!",
  de: 'Türkei mit Premium-Campern entdecken. Abholung in Istanbul, Antalya & mehr. Nur 20% Anzahlung, 24/7 Support. Jetzt Angebot anfordern!',
  es: 'Explora Turquía con nuestras autocaravanas. Entrega en Estambul, Antalya y más. Solo 20% de reserva, soporte 24/7. ¡Pide tu presupuesto!',
  it: 'Esplora la Turchia in Camper & Motorhome. Ritiro a Istanbul, Antalya e altro. Solo 20% di anticipo, supporto 24/7. Chiedi un preventivo!',
  nl: 'Ontdek Turkije met luxe campers. Ophalen in Istanbul, Antalya & meer. Slechts 20% aanbetaling, 24/7 support. Vraag nu een offerte aan!',
  ru: 'Путешествуйте по Турции на автодомах. Стамбул, Анталья и др. Предоплата всего 20%, поддержка 24/7. Получите расчет стоимости сегодня!'
};

export const useSEO = (pageSlug: string) => {
  const [seoData, setSeoData] = useState<SEOData | null>(null);
  const { t, currentLanguage } = useLanguage();

  const localize = (text: string | null) => {
    if (!text) return '';
    if (currentLanguage === 'en') return text;
    return t(text);
  };

  useEffect(() => {
    const fetchSEO = async () => {
      const { data, error } = await supabase
        .from('seo_pages')
        .select('seo_title, seo_description')
        .eq('page_slug', pageSlug)
        .maybeSingle();

      if (!error && data) {
        setSeoData(data);
      }
    };

    const timer = setTimeout(() => {
      fetchSEO();
    }, 8000);

    return () => clearTimeout(timer);
  }, [pageSlug]);

  useLayoutEffect(() => {
    if (!seoData) return;

    if (seoData.seo_title && pageSlug !== '/') {
      document.title = localize(seoData.seo_title);
    }

    if (seoData.seo_description && pageSlug !== '/') {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', localize(seoData.seo_description));
    }

    if (seoData.seo_title && pageSlug !== '/') {
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', localize(seoData.seo_title));
    }

    if (seoData.seo_description && pageSlug !== '/') {
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', localize(seoData.seo_description));
    }

    if (seoData.seo_title && pageSlug !== '/') {
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', localize(seoData.seo_title));
    }

    if (seoData.seo_description && pageSlug !== '/') {
      let twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute('content', localize(seoData.seo_description));
    }
  }, [seoData, currentLanguage, t, pageSlug]);

  useLayoutEffect(() => {
    if (pageSlug !== '/') return;

    const title = HOME_META_TITLES[currentLanguage] || HOME_META_TITLES.en;
    const description = HOME_META_DESCRIPTIONS[currentLanguage] || HOME_META_DESCRIPTIONS.en;

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title);

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', description);

    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', title);

    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDescription) {
      twitterDescription = document.createElement('meta');
      twitterDescription.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDescription);
    }
    twitterDescription.setAttribute('content', description);
  }, [pageSlug, currentLanguage]);
};
